// src/components/assessment/AssessmentTool.jsx
import React, { useEffect, useState } from "react";
import { loadState, refreshState, saveState } from "../../state/stateRepo";
import { useStatePolling } from "../../state/useStatePolling";
import SideNav from "../SideNav";
import ChecklistTab from "./ChecklistTab";
import RadarTab from "./RadarTab";

const PAGE_BG =
  "radial-gradient(circle at 20% 10%, #92bad4, rgba(255,255,255,0.00) 45%), " +
  "radial-gradient(circle at 90% 80%, #92bad4, rgba(244,124,0,0.00) 50%), #ffffff";

export default function AssessmentTool({ projectId }) {
  const [state, setState] = useState(null);
  const [project, setProject] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userEmail, setUserEmail] = useState(null);

  useEffect(() => {
    fetch("/api/projects")
      .then((r) => r.json())
      .then((data) => {
        const items = Array.isArray(data) ? data : data.projects || [];
        const found = items.find((p) => p.ProjectId === projectId);
        if (found) setProject(found);
      })
      .catch((e) => console.error("Failed to load project info:", e));
  }, [projectId]);

  // Fetch logged-in user email
  useEffect(() => {
    fetch("/.auth/me")
      .then((r) => r.json())
      .then((data) => {
        const principal = Array.isArray(data) ? data[0] : data?.clientPrincipal;
        const email =
          principal?.userDetails ||
          principal?.claims?.find((c) => c.typ === "preferred_username" || c.typ === "email")?.val ||
          null;
        if (email) setUserEmail(email);
      })
      .catch((e) => console.error("Failed to load auth info:", e));
  }, []);

  useEffect(() => {
    document.title = "GiGA – EA Dashboard";
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const s = await loadState(projectId);
        if (!cancelled) setState(s);
      } catch (e) {
        console.error("Failed to load state:", e);
        if (!cancelled) setState(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  async function handleSave() {
    if (!state) return;
    setSaving(true);
    try {
      const r = await refreshState(projectId);
      if (r.updated && r.state) {
        window.alert("Er is een recentere versie beschikbaar. Deze wordt nu ingeladen.");
        setState(r.state);
        return;
      }
      await saveState(projectId, state, {
        onConflict: (freshState) => setState(freshState),
        onError: (e) => console.error("Save failed:", e),
      });
    } finally {
      setSaving(false);
    }
  }

  async function doRefresh() {
    try {
      setRefreshing(true);
      const r = await refreshState(projectId);
      if (r.updated && r.state) {
        setState(r.state);
      }
    } catch (e) {
      console.error("Refresh failed:", e);
    } finally {
      setRefreshing(false);
    }
  }

  useStatePolling(projectId, refreshState, setState);

  if (!state) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: PAGE_BG }}>
      <div className="portal-loading">
        <div className="portal-spinner" />
        <p className="portal-loading-text">Project wordt geladen…</p>
      </div>
    </div>
  );

  const activeTab = state.ui?.activeTab ?? "fase1";

  const setActiveTab = (tab) =>
    setState((prev) => ({
      ...prev,
      ui: { ...(prev.ui || {}), activeTab: tab },
    }));

  const checkedActions = state.data?.checkedActions || {};
  const updates = state.data?.updates || {};

  async function autoSave(nextState) {
    try {
      await saveState(projectId, nextState, {
        onConflict: (freshState) => setState(freshState),
        onError: (e) => console.error("Auto-save failed:", e),
      });
    } catch (e) {
      console.error("Auto-save failed:", e);
    }
  }

  const setChecked = (actionId, checked) => {
    const nextState = {
      ...state,
      data: {
        ...(state.data || {}),
        checkedActions: {
          ...(state.data?.checkedActions || {}),
          [actionId]: checked,
        },
      },
    };
    setState(nextState);
    autoSave(nextState);
  };

  const onSaveUpdate = (updateKey, updateObject) => {
    const existing = state.data?.updates?.[updateKey] || [];
    const nextState = {
      ...state,
      data: {
        ...(state.data || {}),
        updates: {
          ...(state.data?.updates || {}),
          [updateKey]: [...existing, updateObject],
        },
      },
    };
    setState(nextState);
    autoSave(nextState);
  };

  const onDeleteUpdate = (updateKey, timestamp) => {
    const existing = state.data?.updates?.[updateKey] || [];
    const nextState = {
      ...state,
      data: {
        ...(state.data || {}),
        updates: {
          ...(state.data?.updates || {}),
          [updateKey]: existing.filter((u) => u.timestamp !== timestamp),
        },
      },
    };
    setState(nextState);
    autoSave(nextState);
  };

  const tabs = [
    { id: "fase1", label: "Fase 1 – Basis op orde" },
    { id: "fase2", label: "Fase 2 – Toepassing in praktijk" },
    { id: "radar", label: "Radaroverzicht" },
  ];

  return (
    <div
      className="min-h-screen"
      style={{ background: PAGE_BG }}
    >
      <SideNav />
      <div className="header-3d">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="dashboard-header-main">
            <div className="dashboard-header-title-group">
              <div className="logo-tile logo-tile--header">
                {project?.LogoPath && (
                  <img
                    className="logo-img"
                    src={process.env.PUBLIC_URL + project.LogoPath}
                    alt={project?.ProjectName ?? projectId}
                  />
                )}
              </div>

              <div>
                <h1 className="text-2xl font-semibold text-white">
                  {project?.ProjectName ?? projectId}
                </h1>

                <p className="mt-1 text-sm text-slate-200">
                  Stuur op enterprise architectuur. Krijg eerst de basis op orde. Pas vervolgens toe op de praktijk.
                </p>
              </div>
            </div>

            <div className="dashboard-header-actions">
              <button
                type="button"
                onClick={doRefresh}
                disabled={refreshing}
                className="login-btn dashboard-header-button"
              >
                {refreshing ? "Vernieuwen..." : "Vernieuwen"}
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="login-btn dashboard-header-button"
              >
                {saving ? "Opslaan..." : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5">
        <div className="card-3d rounded-2xl overflow-hidden" style={{ background: "#31719D" }}>
          {/* Tabs strip */}
          <div className="flex flex-wrap" style={{ background: "#31719D", padding: "10px 14px", gap: "8px" }}>
            {tabs.map((t) => (
              <button
                key={t.id}
                className={
                  "tab-3d text-sm " +
                  (activeTab === t.id ? "active" : "")
                }
                onClick={() => setActiveTab(t.id)}
                type="button"
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="px-4 py-5" style={{ background: "#31719D" }}>
            {activeTab === "fase1" && (
              <ChecklistTab
                phase="level1"
                checkedActions={checkedActions}
                updates={updates}
                userEmail={userEmail}
                onToggle={setChecked}
                onSaveUpdate={onSaveUpdate}
                onSaveDashboard={handleSave}
                onDeleteUpdate={onDeleteUpdate}
              />
            )}

            {activeTab === "fase2" && (
              <ChecklistTab
                phase="level2"
                checkedActions={checkedActions}
                updates={updates}
                userEmail={userEmail}
                onToggle={setChecked}
                onSaveUpdate={onSaveUpdate}
                onSaveDashboard={handleSave}
                onDeleteUpdate={onDeleteUpdate}
              />
            )}

            {activeTab === "radar" && <RadarTab checkedActions={checkedActions} />}
          </div>
        </div>

        <p className="text-[11px] text-center mt-4" style={{ color: "#000000" }}>
          © Datavolwassenheid.nl® B.V. Alle rechten voorbehouden. Dit dashboard en alle daarin opgenomen gegevens, visualisaties, teksten, modellen en methodieken zijn beschermd op grond van de Auteurswet en overige toepasselijke wetgeving. Verveelvoudiging, openbaarmaking, verspreiding of gebruik, geheel of gedeeltelijk, zonder voorafgaande schriftelijke toestemming van Datavolwassenheid.nl B.V. is niet toegestaan.
        </p>
      </div>
    </div>
  );
}